import axios from 'axios'

/**
 * 🔐 GATEWAY PROFILE SERVICE
 * Frontend service for handling gateway profile CRUD operations
 */

// Track if interceptors are already set up globally
let interceptorsSetup = false

class GatewayProfileService {
    constructor() {
        this.baseURL = '/gateway-profile'
        this.setupInterceptors()
    }

    /**
     * Setup axios interceptors for authentication and error handling
     * Only sets up once to avoid duplicate interceptors
     */
    setupInterceptors() {
        if (interceptorsSetup) {
            return
        }

        // Request interceptor to add auth token
        axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('accessToken')
                if (token) {
                    try {
                        config.headers.Authorization = `Bearer ${JSON.parse(token)}`
                    } catch (e) {
                        config.headers.Authorization = `Bearer ${token}`
                    }
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )

        // Response interceptor for error handling
        axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    localStorage.removeItem('accessToken')
                    localStorage.removeItem('userData')
                    window.location.href = '/login'
                }
                return Promise.reject(error)
            }
        )

        interceptorsSetup = true
    }

    /**
     * 🔧 Helper method to handle API errors consistently
     * @param {Error} error - The error object
     * @returns {never} Throws formatted error message as string
     */
    handleError(error) {
        console.error('❌ Gateway Profile API Error:', error)
        const errorMessage = error.response?.data?.message 
            || error.response?.data 
            || error.message 
            || 'An unexpected error occurred'
        throw typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)
    }

    /**
     * ➕ Create a new gateway profile
     * @param {Object} data - Gateway profile data
     * @returns {Promise} API response
     */
    async createGatewayProfile(data) {
        try {
            console.log('➕ Creating gateway profile:', data)
            const response = await axios.post(this.baseURL, data)
            console.log('✅ Gateway profile created successfully')
            return response.data
        } catch (error) {
            this.handleError(error)
        }
    }

    /**
     * 📋 Get all gateway profiles with pagination
     * @param {number} page - Page number
     * @param {number} limit - Items per page
     * @param {string} search - Search term
     * @returns {Promise} API response
     */
    async getAllGatewayProfiles(page = 1, limit = 10, search = '') {
        try {
            console.log('📋 Fetching gateway profiles:', { page, limit, search })
            const response = await axios.get(this.baseURL, {
                params: { page, limit, search }
            })
            console.log('✅ Gateway profiles fetched successfully:', response.data)
            return response.data
        } catch (error) {
            this.handleError(error)
        }
    }

    /**
     * 🔍 Get gateway profile by ID
     * @param {string|number} id - Gateway profile ID
     * @returns {Promise} API response
     */
    async getGatewayProfileById(id) {
        try {
            console.log('🔍 Fetching gateway profile by ID:', id)
            const response = await axios.get(`${this.baseURL}/${id}`)
            console.log('✅ Gateway profile fetched successfully')
            return response.data
        } catch (error) {
            this.handleError(error)
        }
    }

    /**
     * ✏️ Update gateway profile
     * @param {string|number} id - Gateway profile ID
     * @param {Object} data - Updated data
     * @returns {Promise} API response
     */
    async updateGatewayProfile(id, data) {
        try {
            console.log('✏️ Updating gateway profile:', id, data)
            const response = await axios.put(`${this.baseURL}/${id}`, data)
            console.log('✅ Gateway profile updated successfully')
            return response.data
        } catch (error) {
            this.handleError(error)
        }
    }

    /**
     * 🗑️ Delete gateway profile
     * @param {string|number} id - Gateway profile ID
     * @returns {Promise} API response
     */
    async deleteGatewayProfile(id) {
        try {
            console.log('🗑️ Deleting gateway profile:', id)
            const response = await axios.delete(`${this.baseURL}/${id}`)
            console.log('✅ Gateway profile deleted successfully')
            return response.data
        } catch (error) {
            this.handleError(error)
        }
    }

    /**
     * 🔑 Get gateway profile by API key
     * @param {string} apiKey - API key
     * @returns {Promise} API response
     */
    async getGatewayProfileByAPIKey(apiKey) {
        try {
            console.log('🔑 Fetching gateway profile by API key')
            const response = await axios.get(`${this.baseURL}/by-api-key`, {
                params: { apiKey }
            })
            console.log('✅ Gateway profile fetched by API key successfully')
            return response.data
        } catch (error) {
            this.handleError(error)
        }
    }
}

// Create and export singleton instance
const gatewayProfileService = new GatewayProfileService()
export default gatewayProfileService